import { useState, useEffect } from 'react'
import { Users, Plus, Zap, Edit2, Copy, CheckCircle } from 'lucide-react'
import useStore from '../store/useStore'
import { fetchCharacters, createCharacter } from '../api/supabase'

export default function CharacterManager() {
  const { characters, setCharacters, addCharacter, addNotification } = useStore()
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({ name: '', triggerWord: '', loraPath: '', thumbnailUrl: '' })
  const [copied, setCopied] = useState(null)

  useEffect(() => {
    fetchCharacters()
      .then(setCharacters)
      .catch(err => addNotification({ type: 'error', message: `Could not load characters: ${err.message}` }))
      .finally(() => setLoading(false))
  }, [])

  const handleCreate = async () => {
    if (!form.name || !form.triggerWord) {
      addNotification({ type: 'error', message: 'Name and trigger word are required.' })
      return
    }
    try {
      const character = await createCharacter(form)
      addCharacter(character)
      setShowCreate(false)
      setForm({ name: '', triggerWord: '', loraPath: '', thumbnailUrl: '' })
      addNotification({ type: 'success', message: `Character "${character.name}" created.` })
    } catch (err) {
      addNotification({ type: 'error', message: `Failed to create character: ${err.message}` })
    }
  }

  const copyTrigger = (word) => {
    navigator.clipboard.writeText(word)
    setCopied(word)
    setTimeout(() => setCopied(null), 2000)
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white">Character Manager</h2>
          <p className="text-slate-400 text-sm mt-1">Manage LoRA profiles for consistent AI characters.</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)} className="btn-primary">
          <Plus size={16} /> New Character
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="card p-5 mb-6">
          <h3 className="text-sm font-semibold text-white mb-4">Create New Character</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Character Name</label>
              <input className="input" placeholder="e.g. Barry Roach" value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className="label">Trigger Word</label>
              <input className="input" placeholder="e.g. brjroach" value={form.triggerWord}
                onChange={e => setForm(f => ({ ...f, triggerWord: e.target.value }))} />
              <p className="text-xs text-slate-500 mt-1">Unique word — use in every generation prompt</p>
            </div>
            <div>
              <label className="label">LoRA Path (on RunPod volume)</label>
              <input className="input" placeholder="/models/loras/barry_v1.safetensors" value={form.loraPath}
                onChange={e => setForm(f => ({ ...f, loraPath: e.target.value }))} />
            </div>
            <div>
              <label className="label">Thumbnail URL (optional)</label>
              <input className="input" placeholder="https://..." value={form.thumbnailUrl}
                onChange={e => setForm(f => ({ ...f, thumbnailUrl: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button onClick={handleCreate} className="btn-primary">
              <CheckCircle size={15} /> Create Character
            </button>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {/* Training reminder */}
      <div className="card p-4 mb-6 border border-brand-500/20 bg-brand-500/5">
        <div className="flex items-start gap-3">
          <Zap size={16} className="text-brand-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-medium text-brand-300">Ready to train a LoRA?</div>
            <div className="text-xs text-slate-400 mt-1">
              Collect 15-20 source images → Claude captions them → SimpleTuner on RunPod → 45-90 min unattended.
              Cost: ~$0.50. Result: permanent character consistency at 95%+.
            </div>
          </div>
        </div>
      </div>

      {/* Character grid */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading characters...</div>
      ) : characters.length === 0 ? (
        <div className="text-center py-16">
          <Users size={48} className="text-slate-600 mx-auto mb-4" />
          <h3 className="text-white font-medium mb-2">No characters yet</h3>
          <p className="text-slate-400 text-sm mb-4">Create a character profile and train a LoRA to get started.</p>
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus size={16} /> Create First Character
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {characters.map(character => (
            <CharacterCard
              key={character.id}
              character={character}
              onCopyTrigger={copyTrigger}
              copied={copied}
            />
          ))}
        </div>
      )}

    </div>
  )
}

function CharacterCard({ character, onCopyTrigger, copied }) {
  return (
    <div className="card overflow-hidden hover:border-brand-500/30 transition-colors">
      {/* Thumbnail */}
      <div className="aspect-square bg-surface-muted flex items-center justify-center">
        {character.thumbnail_url ? (
          <img src={character.thumbnail_url} alt={character.name}
            className="w-full h-full object-cover" />
        ) : (
          <Users size={32} className="text-slate-600" />
        )}
      </div>
      {/* Info */}
      <div className="p-4">
        <h3 className="text-white font-semibold text-sm">{character.name}</h3>
        <div className="flex items-center gap-2 mt-2">
          <code className="text-xs text-brand-300 bg-brand-500/10 px-2 py-1 rounded flex-1 truncate">
            {character.trigger_word}
          </code>
          <button onClick={() => onCopyTrigger(character.trigger_word)}
            className="text-slate-400 hover:text-brand-400 transition-colors flex-shrink-0">
            {copied === character.trigger_word ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
          </button>
        </div>
        {character.lora_path && (
          <p className="text-xs text-slate-500 mt-2 truncate">{character.lora_path}</p>
        )}
      </div>
    </div>
  )
}
